import React, { ReactNode } from 'react';
import ExportedImage from 'next-image-export-optimizer';
import { css } from '../../styled-system/css';
import { baseFolder } from '../constants';
import Link from 'next/link';
import { Button, Heading } from '@zillow/constellation-10';
import { useIsSpruce } from 'src/hooks/useIsSpruce';

export interface IBethEarnestLayoutProps {
  children: React.ReactNode;
  showFooter?: boolean;
  infoBlock?: React.ReactNode;
  backButtonTarget?: string;
  nextButton?: ReactNode;
}

const commonCss = css.raw({
  background: 'white',
  borderColor: 'gray.300',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-around',
  alignItems: 'center',
  padding: '16px',
});

const HeaderDiv: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div
      className={css({
        ...commonCss,
        borderBottom: '1px solid',
      })}
    >
      {children}
    </div>
  );
};

const FooterDiv: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div
      className={css({
        ...commonCss,
        borderTop: '1px solid',
        position: 'fixed',
        bottom: '0px',
        left: '0px',
        right: '0px',
        zIndex: 1,
      })}
    >
      {children}
    </div>
  );
};

export const BethEarnestLayout: React.FC<IBethEarnestLayoutProps> = ({
  children,
  showFooter = true,
  infoBlock,
  backButtonTarget,
  nextButton,
}) => {
  const isSpruce = useIsSpruce();

  return (
    <>
      <HeaderDiv>
        <div
          className={css({
            display: 'flex',
            width: '100%',
            maxWidth: '1232px',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
          })}
        >
          <Heading level={1}>
            {isSpruce ? ( <Link href={'/'} title="Spruce - Payments">
              <ExportedImage
                src={`${baseFolder}/images/spruce-logo.png`}
                alt={'Spruce - Payments'}
                priority
                width={150}
                height={55}
              />
            </Link>
            ) : (
            <Link href={'/'} title="Zillow Closing - Payments">
              {/* Mobile: Stacked logo */}
              <ExportedImage
                src={`${baseFolder}/images/Zillow-Closing_Primary_Stacked_RGB.svg`}
                alt={'Zillow Closing - Payments'}
                priority
                width={150}
                height={55}
                className={css({ display: 'block', lg: { display: 'none' } })}
                placeholder={'empty'}
              />
              {/* Desktop: Horizontal logo */}
              <ExportedImage
                src={`${baseFolder}/images/Zillow-Closing_Primary_Horizontal_RGB.svg`}
                alt={'Zillow Closing - Payments'}
                priority
                width={250}
                height={35}
                className={css({ display: 'none', lg: { display: 'block', margin: '0 auto', justifyContent: 'center', alignItems: 'center' } })}
                placeholder={'empty'}
              />
            </Link>
          )}
          </Heading>
          {infoBlock}
        </div>
      </HeaderDiv>
      {children}
      {showFooter && (
        <FooterDiv>
          <div
            className={css({
              display: 'flex',
              width: '100%',
              maxWidth: '1232px',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '8px',
            })}
          >
            <div className={css({ flexBasis: '1/2', maxWidth: '200px' })}>
              {backButtonTarget && (
                <Button size={'md'} tone={'brand'} emphasis={'outlined'} fluid asChild>
                  <Link href={backButtonTarget}>Back</Link>
                </Button>
              )}
            </div>
            <div className={css({ flexBasis: '1/2', maxWidth: '200px' })}>{nextButton}</div>
          </div>
        </FooterDiv>
      )}
    </>
  );
};
